import React, { useEffect } from 'react';
import { Form, Input, Select, Row, Col, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { CaseDetail } from '../../../../services/case';
import { CASE_REASONS } from '../../../../constants/caseReasons';
import { formRules } from '../../../../utils/validation';

const { TextArea } = Input;
const { Option } = Select;

// Extended interface to include basic info fields
interface DetailStepData extends Partial<CaseDetail> {
  case_type?: string;
  case_number?: string;
  court_date?: string;
  verdict_date?: string;
}

interface DetailStepProps {
  data: DetailStepData;
  onChange: (data: DetailStepData) => void;
}

// 审级枚举配置
const CASE_LEVEL_OPTIONS = {
  '民事': [
    '一审',
    '二审',
    '申请再审',
    '申请抗议',
    '再审审理',
    '执行',
    '仲裁',
    '劳动仲裁',
    '申诉',
  ],
  '刑事': [
    '侦查',
    '审查起诉',
    '一审',
    '二审',
    '申请再审',
    '再审审理',
    '死刑复核',
    '申诉',
    '刑事速裁',
  ],
  '行政诉讼': [
    '复议',
    '一审',
    '二审',
    '申请再审',
    '申请抗诉',
    '再审审理',
    '执行',
    '申诉',
  ],
};

const DetailStep: React.FC<DetailStepProps> = ({ data, onChange }) => {
  const [form] = Form.useForm();
  const [caseType, setCaseType] = React.useState<string>(data.case_type || '');

  useEffect(() => {
    // Convert date strings to dayjs objects for DatePicker
    const formValues = {
      ...data,
      court_date: data.court_date ? dayjs(data.court_date) : undefined,
      verdict_date: data.verdict_date ? dayjs(data.verdict_date) : undefined,
    };
    form.setFieldsValue(formValues);
    setCaseType(data.case_type || '');
  }, [data, form]);

  const handleValuesChange = (_: any, allValues: any) => {
    // 如果案件类型改变，清空审级
    if (allValues.case_type !== caseType) {
      setCaseType(allValues.case_type);
      form.setFieldsValue({ case_level: undefined });
      allValues.case_level = undefined;
    }

    // Convert dayjs objects back to strings
    const formattedValues = {
      ...allValues,
      court_date: allValues.court_date ? allValues.court_date.format('YYYY-MM-DD HH:mm:ss') : undefined,
      verdict_date: allValues.verdict_date ? allValues.verdict_date.format('YYYY-MM-DD') : undefined,
    };
    onChange(formattedValues);
  };

  // 获取当前案件类型对应的审级选项
  const caseLevelOptions = caseType && CASE_LEVEL_OPTIONS[caseType as keyof typeof CASE_LEVEL_OPTIONS]
    ? CASE_LEVEL_OPTIONS[caseType as keyof typeof CASE_LEVEL_OPTIONS]
    : [];

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={handleValuesChange}
    >
      {/* 案由、案号 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="案由"
            name="case_reason"
            rules={[formRules.required('请选择案由')]}
          >
            <Select
              placeholder="请选择或搜索案由"
              showSearch
              allowClear
              filterOption={(input, option) => {
                const label = String(option?.children || '');
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {CASE_REASONS.map((reason) => (
                <Option key={reason} value={reason}>
                  {reason}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="案号"
            name="case_number"
          >
            <Input placeholder="请输入案号" />
          </Form.Item>
        </Col>
      </Row>

      {/* 案件类型、审级 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="案件类型"
            name="case_type"
            rules={[formRules.required('请选择案件类型')]}
          >
            <Select placeholder="请选择案件类型" allowClear>
              <Option value="民事">民事</Option>
              <Option value="刑事">刑事</Option>
              <Option value="行政诉讼">行政诉讼</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="审级"
            name="case_level"
          >
            <Select 
              placeholder={caseType ? "请选择审级" : "请先选择案件类型"} 
              allowClear
              disabled={!caseType}
            >
              {caseLevelOptions.map((level) => (
                <Option key={level} value={level}>
                  {level}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* 开庭时间、判决日期 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="开庭时间"
            name="court_date"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              placeholder="请选择开庭时间"
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="判决日期"
            name="verdict_date"
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="请选择判决日期"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 法院名称、法院联系方式 */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="法院名称"
            name="court_name"
          >
            <Input placeholder="请输入法院名称" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="法院联系方式" name="court_contact">
            <Input placeholder="请输入法院联系方式" />
          </Form.Item>
        </Col>
      </Row>

      {/* 案件描述 */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label="案件描述"
            name="case_summary"
            tooltip="简要描述案件的基本情况、争议焦点等"
          >
            <TextArea
              placeholder="请输入案件描述"
              rows={4}
              maxLength={2000}
              showCount
            />
          </Form.Item>
        </Col>
      </Row>

      {/* 备注 */}
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label="备注"
            name="notes"
            tooltip="其他需要记录的信息"
          >
            <TextArea
              placeholder="请输入备注信息"
              rows={3}
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default DetailStep;
